import { useEffect, useState } from 'react';
import { CheckCircle2, Loader2, Pencil, Plus, Webhook, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { notasFiscaisApi, mensagemErro } from '@/lib/notasFiscaisApi';
import type { AmbienteFiscal, ConfigFiscal, RegraFiscal, StatusIntegracaoFiscal } from '@/types/notasFiscais';

interface ConfiguracaoFiscalTabProps {
  config: ConfigFiscal | null;
  status: StatusIntegracaoFiscal | null;
  regras: RegraFiscal[];
  onAlterado: () => void;
}

const REGRA_VAZIA: Partial<RegraFiscal> = {
  nome: '',
  descricao: '',
  cfop_interno: '5102',
  cfop_interestadual_contribuinte: '6102',
  cfop_interestadual_nao_contribuinte: '6108',
  csosn: '102',
  pis_cst: '49',
  cofins_cst: '49',
  informacoes_complementares: '',
  padrao: false,
  ativo: true,
};

function Indicador({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className="flex items-center gap-1 text-sm">
      {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-destructive" />}
      {label}
    </span>
  );
}

export function ConfiguracaoFiscalTab({ config, status, regras, onAlterado }: ConfiguracaoFiscalTabProps) {
  const { toast } = useToast();
  const [form, setForm] = useState<Partial<ConfigFiscal>>({});
  const [salvando, setSalvando] = useState(false);
  const [regra, setRegra] = useState<Partial<RegraFiscal> | null>(null);
  const [registrandoWebhook, setRegistrandoWebhook] = useState(false);
  const [inutilizacao, setInutilizacao] = useState({ serie: '1', inicial: '', final: '', justificativa: '' });
  const [inutilizando, setInutilizando] = useState(false);

  useEffect(() => {
    if (config) setForm(config);
  }, [config]);

  const salvarConfig = async (campos: Partial<ConfigFiscal>) => {
    setSalvando(true);
    try {
      await notasFiscaisApi.salvarConfig(campos);
      toast({ title: 'Configuração salva' });
      onAlterado();
    } catch (e) {
      toast({ title: 'Erro ao salvar', description: mensagemErro(e), variant: 'destructive' });
    } finally {
      setSalvando(false);
    }
  };

  const alternarAmbiente = (producao: boolean) => {
    const ambiente: AmbienteFiscal = producao ? 'producao' : 'homologacao';
    const msg = producao
      ? 'Mudar para PRODUÇÃO? As próximas notas terão valor fiscal e serão enviadas à SEFAZ de verdade.'
      : 'Voltar para HOMOLOGAÇÃO? As próximas notas serão apenas testes, sem valor fiscal.';
    if (window.confirm(msg)) salvarConfig({ ambiente });
  };

  const salvarRegra = async () => {
    if (!regra) return;
    setSalvando(true);
    try {
      if (regra.id) await notasFiscaisApi.salvarRegra(regra.id, regra);
      else await notasFiscaisApi.criarRegra(regra);
      toast({ title: 'Regra fiscal salva' });
      setRegra(null);
      onAlterado();
    } catch (e) {
      toast({ title: 'Erro ao salvar regra', description: mensagemErro(e), variant: 'destructive' });
    } finally {
      setSalvando(false);
    }
  };

  const registrarWebhook = async () => {
    setRegistrandoWebhook(true);
    try {
      const res = await notasFiscaisApi.registrarWebhook();
      toast({ title: 'Webhook registrado na Focus', description: `${res.ambiente}: ${res.hook.url}` });
    } catch (e) {
      toast({ title: 'Erro ao registrar webhook', description: mensagemErro(e), variant: 'destructive' });
    } finally {
      setRegistrandoWebhook(false);
    }
  };

  const inutilizar = async () => {
    const ini = Number(inutilizacao.inicial);
    const fim = Number(inutilizacao.final || inutilizacao.inicial);
    if (!window.confirm(`Inutilizar a numeração ${ini} a ${fim} da série ${inutilizacao.serie}? Esta ação é definitiva.`)) return;
    setInutilizando(true);
    try {
      await notasFiscaisApi.inutilizar({
        serie: Number(inutilizacao.serie),
        numero_inicial: ini,
        numero_final: fim,
        justificativa: inutilizacao.justificativa,
      });
      toast({ title: 'Numeração inutilizada' });
      setInutilizacao({ serie: inutilizacao.serie, inicial: '', final: '', justificativa: '' });
    } catch (e) {
      toast({ title: 'Erro ao inutilizar', description: mensagemErro(e), variant: 'destructive' });
    } finally {
      setInutilizando(false);
    }
  };

  const setR = <K extends keyof RegraFiscal>(campo: K, valor: RegraFiscal[K]) =>
    setRegra((atual) => (atual ? { ...atual, [campo]: valor } : atual));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Integração Focus NFe</CardTitle>
          <CardDescription>Emitente: Sun7 Energy Comercial LTDA - CNPJ 69.239.016/0001-58 - Simples Nacional</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-4">
            <Indicador ok={Boolean(status?.homologacao_configurado)} label="Token de homologação" />
            <Indicador ok={Boolean(status?.producao_configurado)} label="Token de produção" />
            <Indicador ok={Boolean(status?.webhook_configurado)} label="Segredo do webhook" />
          </div>
          <div className="flex items-center gap-3 rounded-md border p-3">
            <Switch
              checked={config?.ambiente === 'producao'}
              disabled={salvando || !config || Boolean(status?.producao_obrigatoria)}
              onCheckedChange={alternarAmbiente}
            />
            <div>
              <p className="font-medium">
                Ambiente atual:{' '}
                {config?.ambiente === 'producao' ? (
                  <Badge variant="destructive">PRODUÇÃO</Badge>
                ) : (
                  <Badge variant="secondary">Homologação (teste)</Badge>
                )}
              </p>
              <p className="text-xs text-muted-foreground">
                {status?.producao_obrigatoria
                  ? 'Neste sistema em produção, a NF-e é enviada à SEFAZ e tem valor fiscal. A homologação fica só no desenvolvimento.'
                  : 'Mude para produção só depois de validar as regras com o contador. Em produção a nota tem valor fiscal.'}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="outline" onClick={registrarWebhook} disabled={registrandoWebhook}>
              {registrandoWebhook ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Webhook className="h-4 w-4 mr-1" />}
              Registrar webhook no ambiente atual
            </Button>
            <span className="text-xs text-muted-foreground break-all">{status?.webhook_url || 'URL pública do backend não configurada'}</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2 space-y-1">
              <Label>Natureza da operação padrão</Label>
              <Input
                maxLength={60}
                value={form.natureza_operacao || ''}
                onChange={(e) => setForm({ ...form, natureza_operacao: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label>Tributos aproximados - Lei 12.741 (%)</Label>
              <Input
                type="number"
                min={0}
                max={100}
                step="0.01"
                value={form.aliquota_tributos_aproximados ?? ''}
                onChange={(e) =>
                  setForm({ ...form, aliquota_tributos_aproximados: e.target.value === '' ? undefined : Number(e.target.value) })
                }
              />
              <p className="text-xs text-muted-foreground">
                Alíquota efetiva do Simples do último mês apurado. Atualize todo mês (4% no primeiro mês).
              </p>
            </div>
            <div className="space-y-1">
              <Label>Alíquota de crédito de ICMS (%)</Label>
              <Input
                type="number"
                min={0}
                max={100}
                step="0.01"
                placeholder="Não usado hoje"
                value={form.aliquota_credito_simples ?? ''}
                onChange={(e) =>
                  setForm({ ...form, aliquota_credito_simples: e.target.value === '' ? null : Number(e.target.value) })
                }
              />
              <p className="text-xs text-muted-foreground">
                Só para CSOSN 101 (venda a contribuinte que vai revender). A Sun7 usa CSOSN 102.
              </p>
            </div>
            <div className="md:col-span-3 space-y-1">
              <Label>Informações complementares em todas as notas (opcional)</Label>
              <Textarea
                rows={2}
                value={form.informacoes_complementares_padrao || ''}
                onChange={(e) => setForm({ ...form, informacoes_complementares_padrao: e.target.value })}
              />
            </div>
          </div>
          <Button
            onClick={() =>
              salvarConfig({
                natureza_operacao: form.natureza_operacao,
                aliquota_credito_simples: form.aliquota_credito_simples ?? null,
                aliquota_tributos_aproximados: form.aliquota_tributos_aproximados,
                informacoes_complementares_padrao: form.informacoes_complementares_padrao ?? null,
              })
            }
            disabled={salvando}
          >
            {salvando && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Salvar configuração
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <CardTitle>Regras fiscais</CardTitle>
            <CardDescription>
              CFOP, CSOSN e PIS/COFINS aplicados aos itens, conforme orientação do contador.
            </CardDescription>
          </div>
          <Button onClick={() => setRegra({ ...REGRA_VAZIA })}><Plus className="h-4 w-4 mr-1" /> Nova regra</Button>
        </CardHeader>
        <CardContent>
          <div className="border rounded-md overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>CFOP SC</TableHead>
                  <TableHead>CFOP fora (contrib.)</TableHead>
                  <TableHead>CFOP fora (não contrib.)</TableHead>
                  <TableHead>CSOSN</TableHead>
                  <TableHead>PIS/COFINS</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {regras.map((r) => (
                  <TableRow key={r.id} className={r.ativo ? '' : 'opacity-50'}>
                    <TableCell>
                      {r.nome} {r.padrao && <Badge variant="secondary" className="ml-1">Padrão</Badge>}
                    </TableCell>
                    <TableCell>{r.cfop_interno}</TableCell>
                    <TableCell>{r.cfop_interestadual_contribuinte}</TableCell>
                    <TableCell>{r.cfop_interestadual_nao_contribuinte}</TableCell>
                    <TableCell>{r.csosn}</TableCell>
                    <TableCell>{r.pis_cst} / {r.cofins_cst}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => setRegra({ ...r })}><Pencil className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Inutilizar numeração</CardTitle>
          <CardDescription>
            Use apenas quando um número foi pulado e nunca será usado. A Focus controla a numeração automaticamente.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div className="space-y-1">
            <Label>Série</Label>
            <Input value={inutilizacao.serie} onChange={(e) => setInutilizacao({ ...inutilizacao, serie: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label>Nº inicial</Label>
            <Input value={inutilizacao.inicial} onChange={(e) => setInutilizacao({ ...inutilizacao, inicial: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label>Nº final</Label>
            <Input value={inutilizacao.final} onChange={(e) => setInutilizacao({ ...inutilizacao, final: e.target.value })} />
          </div>
          <div className="md:col-span-2 space-y-1">
            <Label>Justificativa (mín. 15 caracteres)</Label>
            <Input
              value={inutilizacao.justificativa}
              onChange={(e) => setInutilizacao({ ...inutilizacao, justificativa: e.target.value })}
            />
          </div>
          <Button
            variant="destructive"
            onClick={inutilizar}
            disabled={inutilizando || !inutilizacao.inicial || inutilizacao.justificativa.trim().length < 15}
          >
            {inutilizando && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Inutilizar
          </Button>
        </CardContent>
      </Card>

      <Dialog open={Boolean(regra)} onOpenChange={(open) => !open && setRegra(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{regra?.id ? 'Editar regra fiscal' : 'Nova regra fiscal'}</DialogTitle>
          </DialogHeader>
          {regra && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-3 space-y-1">
                <Label>Nome</Label>
                <Input value={regra.nome || ''} onChange={(e) => setR('nome', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>CFOP dentro de SC</Label>
                <Input value={regra.cfop_interno || ''} onChange={(e) => setR('cfop_interno', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>CFOP outro estado (contribuinte)</Label>
                <Input
                  value={regra.cfop_interestadual_contribuinte || ''}
                  onChange={(e) => setR('cfop_interestadual_contribuinte', e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label>CFOP outro estado (não contribuinte)</Label>
                <Input
                  value={regra.cfop_interestadual_nao_contribuinte || ''}
                  onChange={(e) => setR('cfop_interestadual_nao_contribuinte', e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label>CSOSN</Label>
                <Input value={regra.csosn || ''} onChange={(e) => setR('csosn', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>CST PIS</Label>
                <Input value={regra.pis_cst || ''} onChange={(e) => setR('pis_cst', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>CST COFINS</Label>
                <Input value={regra.cofins_cst || ''} onChange={(e) => setR('cofins_cst', e.target.value)} />
              </div>
              <div className="md:col-span-3 space-y-1">
                <Label>Texto legal na nota (opcional)</Label>
                <Textarea
                  rows={2}
                  value={regra.informacoes_complementares || ''}
                  onChange={(e) => setR('informacoes_complementares', e.target.value)}
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={Boolean(regra.padrao)} onCheckedChange={(v) => setR('padrao', v)} /> Regra padrão
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={regra.ativo !== false} onCheckedChange={(v) => setR('ativo', v)} /> Ativa
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegra(null)} disabled={salvando}>Cancelar</Button>
            <Button onClick={salvarRegra} disabled={salvando}>
              {salvando && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
